import React, { useState, useRef, useEffect } from "react";
import { buildChain, roundMaxItems } from "../lib/chain";
import { createPortal } from "react-dom";
const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const ANGLE_LABELS = { former: "Formers", competitor: "Competitors", customer: "Customers" };

// ── Source flow D: presentation constants (UI only) ──────────────────────────
// Step index per stage; step 4 ("done") means all four pills are complete.
const STEP_OF = { configure: 0, proposing: 0, signoff: 1, probing: 1, counts: 2, pulling: 3, done: 4 };
const STEP_LABELS = ["Configure", "Sign off", "Counts", "Fetch"];
const STAGE_META = {
  configure: "Step 1 of 4",
  proposing: "Step 1 of 4 · working…",
  signoff: "Step 2 of 4",
  probing: "Step 2 of 4 · working…",
  counts: "Step 3 of 4",
  pulling: "Step 4 of 4 · working…",
  done: "Done",
};
const STAGE_COPY = {
  configure: ["Set up your search", "Tell us the company and what you want to learn. Rasp drafts the rest."],
  proposing: ["Drafting your plan", "This takes a few seconds."],
  signoff: ["Review the plan", "Change anything you like — nothing has been fetched yet."],
  probing: ["Counting matches", "A quick check before anything is pulled."],
  counts: ["Choose how many to bring in", "You stay in control of coverage and cost."],
  pulling: ["Bringing in your leads", "This can take a minute or two."],
  done: ["You're all set", "Your new batch is ready to rank."],
};
const ANGLE_HINT = "Formers = people who used to work there. Competitors and customers search adjacent companies instead.";
// Async stage → the stage the real machine moves to when it succeeds. The loading
// screen holds ~350ms on "complete" before the next stage is shown.
const ASYNC_NEXT = { proposing: "signoff", probing: "counts", pulling: "done" };
const COMPLETE_HOLD_MS = 350;
// Expected durations drive the ring and ETA only where no real progress exists.
// They are estimates, not measurements — tune from real runs.
const EXPECT_MS = { proposing: 8000, probeSet: 14000, pullRun: 90000 };
const LI_PAGE_SIZE = 25;
const SNAPSHOT_KEY = "nsp_inflight_v1";
const RING_C = 2 * Math.PI * 54; // r=54
const extractDomain = (url) => {
  if (!url) return url;
  try { return new URL(url).hostname; } catch { return url; }
};

const fmtN = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const makeCacheKey = (cs) => JSON.stringify({ t: [...cs.titles].sort(), e: [...cs.exclude].sort() });
const wordCount = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;

// ── Company dedup helpers (mirrors backend _is_same_company) ──────────────────
const CO_SUFFIX_TOKENS = new Set(['inc','llc','ltd','corp','co','company','group','holdings','plc','gmbh','sas','bv']);
const normCompany = (name) => {
  const tokens = name.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/).filter(Boolean);
  return new Set(tokens.filter((t) => !CO_SUFFIX_TOKENS.has(t)));
};
const normLinkedInSlug = (url) => {
  if (!url) return null;
  const m = url.toLowerCase().split('?')[0].split('#')[0].match(/\/company\/([^/]+)/);
  return m ? m[1].replace(/\/+$/, '') : null;
};
const sameCompany = (a, b) => {
  const as_ = normLinkedInSlug(a.url);
  const bs_ = normLinkedInSlug(b.url);
  if (as_ && bs_) return as_ === bs_;
  const at = normCompany(a.name);
  const bt = normCompany(b.name);
  if (at.size !== bt.size) return false;
  for (const t of at) if (!bt.has(t)) return false;
  return true;
};

// ── Chip popover (portal) ─────────────────────────────────────────────────────
function ChipPopover({ anchor, chip, onSave, onRemoveLink, onRemoveChip, onClose }) {
  const ref = useRef(null);
  const [urlInput, setUrlInput] = useState(chip.url || "");
  const [urlError, setUrlError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  const validate = (v) =>
    v.trim() && !v.includes("linkedin.com/company/")
      ? "Must be a LinkedIn company URL (linkedin.com/company/…)"
      : null;

  const handleSave = () => {
    const err = validate(urlInput);
    if (err) { setUrlError(err); return; }
    onSave(urlInput.trim() || null);
  };

  const left = Math.min(Math.max(8, anchor.left), window.innerWidth - 282);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: anchor.bottom + 6,
        left,
        zIndex: 9999,
        minWidth: 274,
        background: "var(--glass-card)",
        border: "1px solid var(--line)",
        borderRadius: 10,
        padding: 12,
        boxShadow: "var(--card-shadow)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)", marginBottom: 6 }}>
        {chip.name}
      </div>
      <input
        autoFocus
        type="text"
        placeholder="linkedin.com/company/…"
        value={urlInput}
        onChange={(e) => { setUrlInput(e.target.value); setUrlError(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
        style={{ width: "100%", boxSizing: "border-box", height: 32, background: "var(--input-bg)", border: "1px solid var(--line)", borderRadius: 8, padding: "0 10px", fontSize: 12, color: "var(--text-1)", marginBottom: urlError ? 2 : 6 }}
      />
      {urlError && (
        <div style={{ fontSize: 10, color: "var(--negative)", marginBottom: 4 }}>{urlError}</div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button className="r-btn r-btn-primary" onClick={handleSave} style={{ height: 26, fontSize: 11, padding: "0 10px" }}>
          Save link
        </button>
        {chip.url && (
          <button
            onClick={() => { onRemoveLink(); onClose(); }}
            className="r-btn r-btn-ghost"
            style={{ height: 26, fontSize: 11 }}
          >
            Remove link
          </button>
        )}
        <button
          onClick={() => { onRemoveChip(); onClose(); }}
          className="r-btn r-btn-ghost"
          style={{ height: 26, fontSize: 11, color: "var(--negative)" }}
        >
          Remove chip
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── Chip ──────────────────────────────────────────────────────────────────────
// tone: undefined = default chip; "committed" = configure-stage company chip
// (teal, dot, ×); "target" = sign-off company chip (amber + hollow dot until
// linked, then teal + dot + "linked").
function Chip({ chip, onUpdate, onRemove, warnMixed = false, proposed = false, struck = false, onStrike = null, onClear = null, tone }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const ref = useRef(null);

  const isLinked = !!chip.url;

  const border = struck
    ? "1px solid var(--line)"
    : tone === "committed"
    ? "1px solid rgba(45,212,191,.28)"
    : tone === "target"
    ? (isLinked ? "1px solid rgba(45,212,191,.28)" : "1px solid rgba(224,160,80,.5)")
    : warnMixed
    ? "1px solid var(--warning)"
    : isLinked
    ? "1px solid rgba(45,212,191,.28)"
    : proposed
    ? "1px dashed rgba(45,212,191,.28)"
    : "1px solid var(--line)";

  const bg = !struck && (isLinked || tone === "committed") ? "rgba(45,212,191,.08)" : "var(--input-bg)";
  const dotFilled = tone === "committed" || isLinked;

  const handleClick = () => {
    if (struck) return;
    if (ref.current) setAnchor(ref.current.getBoundingClientRect());
    setOpen(true);
  };

  return (
    <>
      <div
        ref={ref}
        onClick={handleClick}
        title={
          struck ? chip.name
          : warnMixed ? "Name-match only — link its LinkedIn page for exact matching."
          : isLinked ? chip.url
          : "Click to add LinkedIn URL"
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "0 8px",
          height: 24,
          borderRadius: 4,
          border,
          background: bg,
          fontSize: 12,
          color: struck ? "var(--text-5)" : "var(--text-1)",
          cursor: struck ? "default" : "pointer",
          textDecoration: struck ? "line-through" : "none",
          opacity: struck ? 0.5 : 1,
          userSelect: "none",
          flexShrink: 0,
          transition: "border-color .25s, background .25s",
          animation: tone === "committed" ? "nsp-popIn .25s ease-out" : undefined,
        }}
      >
        {!struck && (
          <span style={{
            width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
            background: dotFilled ? "var(--accent)" : "none",
            border: dotFilled ? "none" : "1px solid var(--text-3)",
          }} />
        )}
        <span>{chip.name}</span>
        {tone === "target" && isLinked && (
          <span style={{ fontSize: 10, fontFamily: "var(--font-data)", color: "var(--accent)" }}>linked</span>
        )}
        {proposed && !struck && (
          <span style={{ fontSize: 10, fontFamily: "var(--font-data)", color: "var(--accent)", marginLeft: 1 }}>proposed</span>
        )}
        {onStrike && (
          <span
            onClick={(e) => { e.stopPropagation(); onStrike(); }}
            style={{ marginLeft: 2, cursor: "pointer", color: "var(--text-4)", lineHeight: 1, fontSize: 13 }}
          >
            {struck ? "↩" : "×"}
          </span>
        )}
        {onClear && (
          <span
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            title="Clear"
            style={{ marginLeft: 2, cursor: "pointer", color: "var(--text-4)", lineHeight: 1, fontSize: 13 }}
          >
            ×
          </span>
        )}
      </div>
      {open && anchor && (
        <ChipPopover
          anchor={anchor}
          chip={chip}
          onSave={(url) => { onUpdate({ ...chip, url }); setOpen(false); }}
          onRemoveLink={() => onUpdate({ ...chip, url: null })}
          onRemoveChip={() => { onRemove(); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── Small presentational pieces ───────────────────────────────────────────────
function Tick({ color = "#04201d", w = 4, h = 8, stroke = 1.5, mt = -2 }) {
  return (
    <span style={{
      width: w, height: h, display: "block", marginTop: mt,
      borderRight: `${stroke}px solid ${color}`, borderBottom: `${stroke}px solid ${color}`,
      transform: "rotate(45deg)",
    }} />
  );
}

// SOURCE-SHEET-1: the stepper lives in the page header — four labels, four
// 64×3 segments, and the STAGE_META line. Each label carries data-step-state.
function HeaderStepper({ step, meta }) {
  const teal = "var(--accent)";
  const grey = "rgba(130,210,220,.2)";
  const half = "linear-gradient(90deg, #2dd4bf 55%, rgba(130,210,220,.2) 55%)";
  const stateOf = (i) => (i < step ? "done" : i === step ? "active" : "todo");
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, paddingBottom: 2, flex: "none" }}>
      <div style={{ display: "flex", gap: 14, fontSize: 11, fontWeight: 500 }}>
        {STEP_LABELS.map((label, i) => {
          const s = stateOf(i);
          return (
            <span key={label} data-step-state={s} style={{ color: s === "done" ? teal : s === "active" ? "var(--text-1)" : "var(--text-4)", transition: "color .4s" }}>
              {label}
            </span>
          );
        })}
      </div>
      <div aria-hidden="true" style={{ display: "flex", gap: 4 }}>
        {STEP_LABELS.map((label, i) => {
          const s = stateOf(i);
          return (
            <span key={label} style={{ width: 64, height: 3, borderRadius: 2, background: s === "done" ? teal : s === "active" ? half : grey, transition: "background .4s" }} />
          );
        })}
      </div>
      <span style={{ fontSize: 11, color: "var(--text-4)" }}>{meta}</span>
    </div>
  );
}

function ChecklistRow({ label, state, value, valueColor }) {
  const isDone = state === "done";
  const isActive = state === "active";
  const border = isDone ? "rgba(69,196,154,.3)" : isActive ? "rgba(45,212,191,.45)" : "rgba(130,210,220,.14)";
  return (
    <div data-row-state={state} style={{
      display: "flex", alignItems: "center", gap: 10, minHeight: 36, padding: "0 12px", borderRadius: 8,
      border: `1px solid ${border}`,
      background: isActive ? "rgba(45,212,191,.08)" : "rgba(255,255,255,.02)",
      fontSize: 13, color: state === "todo" ? "var(--text-4)" : "var(--text-1)", transition: "all .4s",
    }}>
      <span style={{
        width: 16, height: 16, borderRadius: "50%", flex: "none", display: "flex", alignItems: "center", justifyContent: "center",
        background: isDone ? "var(--positive)" : "transparent", border: `1px solid ${border}`,
      }}>
        {isDone && <Tick w={4} h={7} mt={-1} />}
        {isActive && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)", display: "block", animation: "rasp-pulse 1s ease-in-out infinite" }} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {value != null && value !== "" && (
        <span style={{ fontFamily: "var(--font-data)", fontSize: 12, fontWeight: 500, color: valueColor || "var(--text-1)", flex: "none" }}>{value}</span>
      )}
    </div>
  );
}

// Loading sheet used by proposing / probing / pulling: ring on the left, a
// column of title · status · checklist · eta on the right. Left-aligned.
function LoadingScreen({ title, status, reassurance, progress, rows, eta }) {
  const p = Math.max(0, Math.min(1, progress || 0));
  return (
    <div data-testid="nsp-loading" className="nsp-sheet" style={{
      display: "flex", alignItems: "flex-start", gap: 20, padding: "22px 18px",
      animation: "nsp-slideIn .4s cubic-bezier(.2,.7,.2,1)",
    }}>
      <svg width="44" height="44" viewBox="0 0 120 120" style={{ flex: "none", transform: "rotate(-90deg)" }}>
        <defs>
          <linearGradient id="nspRingGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0e7490" />
            <stop offset="1" stopColor="#5eead4" />
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="9" />
        <circle
          data-testid="nsp-ring"
          cx="60" cy="60" r="54" fill="none" stroke="url(#nspRingGrad)" strokeWidth="9" strokeLinecap="round"
          strokeDasharray={RING_C.toFixed(1)} strokeDashoffset={(RING_C * (1 - p)).toFixed(1)}
          style={{ transition: "stroke-dashoffset .5s ease" }}
        />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-.01em", color: "var(--text-1)" }}>{title}</div>
        <div style={{ fontSize: 13, color: "var(--text-3)", lineHeight: 1.5 }}>{status}</div>
        {reassurance && <div style={{ fontSize: 12, color: "var(--text-4)" }}>{reassurance}</div>}
        {rows.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 460, marginTop: 4 }}>
            {rows.map((r, i) => <ChecklistRow key={i} {...r} />)}
          </div>
        )}
        <span style={{ fontSize: 12, color: "var(--text-4)" }}>{eta}</span>
      </div>
    </div>
  );
}

const Mono = ({ children, color = "var(--text-1)" }) => (
  <span style={{ fontFamily: "var(--font-data)", color }}>{children}</span>
);

// Title chips: a read-only view over a group's titles. Collapsed shows the
// first CHIP_PREVIEW titles and a "+N" chip; expanded shows them all.
const CHIP_PREVIEW = 3;
function TitleChips({ titles, expanded, onMore, moreTestId }) {
  const shown = expanded ? titles : titles.slice(0, CHIP_PREVIEW);
  const hidden = titles.length - shown.length;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, minWidth: 0 }}>
      {shown.map((t, k) => <span key={k} className="nsp-chip">{t}</span>)}
      {hidden > 0 && (
        onMore
          ? <button type="button" className="nsp-chip-more" data-testid={moreTestId} onClick={onMore} title="Show all titles">+{hidden}</button>
          : <span className="nsp-chip-more" data-testid={moreTestId} style={{ cursor: "default" }}>+{hidden}</span>
      )}
    </div>
  );
}

const CRUMB_ANGLE = { former: "former employees", competitor: "competitors", customer: "customers" };

const autoSize = (el) => { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; };

// ── NewSearchPanel ─────────────────────────────────────────────────────────────
// onStageChange(stage, batchId) and onOpenBatch(batchId) are optional, UI-only
// callbacks (Source flow D): SourceView uses them to dim the batch list while a
// search runs, highlight the new batch, and open it from "Rank my leads".
export default function NewSearchPanel({ token, onIngested, onStageChange, onOpenBatch, rankStatus }) {
  const [stage, setStage] = useState("configure");

  // Configure inputs — preserved through Back
  const [angle, setAngle] = useState("former");
  const [targetChip, setTargetChip] = useState(null);
  const [targetInput, setTargetInput] = useState("");
  const [companyChips, setCompanyChips] = useState([]);
  const [companyInput, setCompanyInput] = useState("");
  const [titlesInput, setTitlesInput] = useState("");
  const [contextInput, setContextInput] = useState("");
  const [excludeHr, setExcludeHr] = useState(true);
  const [excludeJunior, setExcludeJunior] = useState(true);

  // Signoff state — populated from propose response
  const [planWarnings, setPlanWarnings] = useState([]);
  const [signoffTarget, setSignoffTarget] = useState(null);
  const [signoffCompanies, setSignoffCompanies] = useState([]);
  const [sfCompanyInput, setSfCompanyInput] = useState("");
  const [addDupWarning, setAddDupWarning] = useState(null);
  const [sets, setSets] = useState([{ titles: "" }]);
  const [searchContext, setSearchContext] = useState("");
  const [sfExcludeHr, setSfExcludeHr] = useState(true);
  const [sfExcludeJunior, setSfExcludeJunior] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Captured propose response — sent as plan in batch POST
  const [proposedPlan, setProposedPlan] = useState(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // Runtime
  const [batchId, setBatchId] = useState(null);
  const [probeSets, setProbeSets] = useState([]);
  const [probeResults, setProbeResults] = useState([]);
  const [fetchCaps, setFetchCaps] = useState([]);
  const [groupEnabled, setGroupEnabled] = useState([]);
  const [uniqueCounts, setUniqueCounts] = useState(null);
  const [uniqueNeedsUpdate, setUniqueNeedsUpdate] = useState(false);
  const [uniqueUpdating, setUniqueUpdating] = useState(false);
  const [countsExpandedGroups, setCountsExpandedGroups] = useState(new Set());
  const [uniqueCountsCache, setUniqueCountsCache] = useState({});
  const [autoRecountSignal, setAutoRecountSignal] = useState(0);
  const [probingUnique, setProbingUnique] = useState(false);
  const [pullResults, setPullResults] = useState([]);
  const [pullRuns, setPullRuns] = useState([]);
  const [progressMsg, setProgressMsg] = useState("");
  const [error, setError] = useState(null);
  const [tightenBanner, setTightenBanner] = useState(null); // {text, prevSets}
  const [tightenLoading, setTightenLoading] = useState(false);
  const [tightenError, setTightenError] = useState(null);
  const [creditsMe, setCreditsMe] = useState(null);

  const autoRankCalledRef = useRef(false);
  const autoRecountTimerRef = useRef(null);

  // ── UI-only state (Source flow D) — drives presentation, never an API call ──
  const [shownStage, setShownStage] = useState(stage);
  const [holding, setHolding] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false); // sign-off: topic textarea revealed by "Edit"
  const [probeLive, setProbeLive] = useState([]);   // probe results as each set finishes
  const [pullLive, setPullLive] = useState({});     // pull results keyed by page index as each run finishes
  const [now, setNow] = useState(() => Date.now());
  const stageStartRef = useRef(Date.now());
  const probeStepStartRef = useRef(Date.now());
  const busy = !!ASYNC_NEXT[shownStage];

  // Follow the real stage; after an async stage succeeds, hold its loading screen
  // on "complete" for COMPLETE_HOLD_MS before showing the next stage.
  useEffect(() => {
    if (stage === shownStage) { setHolding(false); return undefined; }
    if (ASYNC_NEXT[shownStage] === stage && !error) {
      setHolding(true);
      const t = setTimeout(() => { setHolding(false); setShownStage(stage); }, COMPLETE_HOLD_MS);
      return () => clearTimeout(t);
    }
    setHolding(false);
    setShownStage(stage);
    return undefined;
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    stageStartRef.current = Date.now();
    probeStepStartRef.current = Date.now();
    setNow(Date.now());
  }, [shownStage]);
  useEffect(() => { probeStepStartRef.current = Date.now(); }, [probeLive.length]);
  useEffect(() => {
    if (!busy || holding) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [busy, holding]);
  useEffect(() => {
    if (onStageChange) onStageChange(shownStage, batchId);
  }, [shownStage, batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch credits balance when first entering the counts stage.
  // creditsMe guard: skip on re-entry (e.g. after a 402 returned updated balance).
  useEffect(() => {
    if (stage !== "counts" || creditsMe !== null) return;
    (async () => {
      try {
        const r = await fetch(`${API}/credits/me`, { headers });
        if (r.ok) {
          const data = await r.json();
          if (typeof data.balance === 'number') setCreditsMe(data);
        }
      } catch (_) {}
    })();
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // V2: auto-recount 1,200ms after the last toggle.
  useEffect(() => {
    if (autoRecountSignal === 0) return;
    const anyEnabled = probeSets.some((_, i) => groupEnabled[i] ?? true);
    if (!anyEnabled) return;
    handleUpdateUnique();
  }, [autoRecountSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist in-flight search so navigation doesn't lose progress.
  useEffect(() => {
    if (stage === "done") {
      try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (_) {}
      return;
    }
    if (stage !== "counts" && stage !== "pulling") return;
    try {
      sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({
        stage, batchId, angle, signoffTarget, signoffCompanies,
        sets, searchContext, sfExcludeHr, sfExcludeJunior,
        probeSets, probeResults, fetchCaps, groupEnabled, uniqueCounts,
      }));
    } catch (_) {}
  }, [stage, batchId, angle, signoffTarget, signoffCompanies, sets, searchContext, sfExcludeHr, sfExcludeJunior, probeSets, probeResults, fetchCaps, groupEnabled, uniqueCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: restore a snapshot left by a previous navigation.
  useEffect(() => {
    let snap;
    try { snap = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || "null"); } catch (_) {}
    if (!snap || (snap.stage !== "counts" && snap.stage !== "pulling")) return;
    setBatchId(snap.batchId ?? null);
    setAngle(snap.angle ?? "former");
    setSignoffTarget(snap.signoffTarget ?? null);
    setSignoffCompanies(snap.signoffCompanies ?? []);
    setSets(snap.sets ?? [{ titles: "" }]);
    setSearchContext(snap.searchContext ?? "");
    setSfExcludeHr(snap.sfExcludeHr ?? true);
    setSfExcludeJunior(snap.sfExcludeJunior ?? true);
    setProbeSets(snap.probeSets ?? []);
    setProbeResults(snap.probeResults ?? []);
    setFetchCaps(snap.fetchCaps ?? []);
    if (snap.groupEnabled) setGroupEnabled(snap.groupEnabled);
    if (snap.uniqueCounts) setUniqueCounts(snap.uniqueCounts);
    if (snap.stage === "counts") { setStage("counts"); return; }
    // stage === "pulling": restore state and resume polling existing runs
    setStage("pulling");
    setProgressMsg("Resuming…");
    (async () => {
      const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      let runs;
      try {
        const r = await fetch(`${API}/sourcing-batches/${snap.batchId}/apify-runs`);
        if (!r.ok) { setStage("done"); return; }
        runs = await r.json();
      } catch (_) { setStage("done"); return; }
      if (!runs || !runs.length) { setStage("done"); return; }
      const resultArr = runs.map(() => ({ staged: 0, dupes: 0, error: null }));
      const isTerminal = (s) => s === "ingested" || s === "partial" || s === "failed";
      await Promise.allSettled(
        runs.map(async (run, i) => {
          try {
            if (isTerminal(run.status)) {
              resultArr[i] = {
                staged: run.staged ?? 0,
                dupes: run.intra_batch_dupes ?? 0,
                error: run.status === "failed" ? run.error || "Run failed" : null,
              };
            } else {
              while (true) {
                await new Promise((res) => setTimeout(res, 10000));
                const cr = await fetch(`${API}/apify-runs/${run.id}/check`, { method: "POST", headers: h });
                const cd = await cr.json();
                if (isTerminal(cd.status)) {
                  resultArr[i] = {
                    staged: cd.staged ?? 0,
                    dupes: cd.intra_batch_dupes ?? 0,
                    error: cd.status === "failed" ? cd.error || "Run failed" : null,
                  };
                  break;
                }
              }
            }
            setPullLive((p) => ({ ...p, [i]: resultArr[i] }));
          } catch (_) {
            resultArr[i] = { staged: 0, dupes: 0, error: "Polling error" };
            setPullLive((p) => ({ ...p, [i]: resultArr[i] }));
          }
        })
      );
      setPullRuns(runs);
      setPullResults(resultArr);
      setProgressMsg("");
      setStage("done");
      if (onIngested) onIngested();
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // ── Derived ───────────────────────────────────────────────────────────────
  const activeCompanies = signoffCompanies.filter((c) => !c.struck);
  const validSets = sets.filter((s) => s.titles.trim().length > 0);
  const nameMatchCompanies = activeCompanies.filter((c) => !c.url);
  const maxSets = angle === "customer" ? 1 : 4;
  const anyConfigCompanyLinked = companyChips.some((c) => !!c.url);
  const anySignoffCompanyLinked = activeCompanies.some((c) => !!c.url);
  const planSourceCompanies = signoffCompanies.filter((c) => c.proposed && c.source_url);

  const canPropose =
    !!targetChip &&
    (angle !== "customer" || contextInput.trim().length > 0);

  const runsCount = angle === "former" ? validSets.length : activeCompanies.length;

  const unlinkedCount =
    (signoffTarget && !signoffTarget.url ? 1 : 0) +
    (angle !== "former" ? signoffCompanies.filter((c) => !c.struck && !c.url).length : 0);
  const dupCompanyPair = (() => {
    const active = signoffCompanies.filter((c) => !c.struck);
    for (let i = 0; i < active.length; i++)
      for (let j = i + 1; j < active.length; j++)
        if (sameCompany(active[i], active[j])) return [active[i].name, active[j].name];
    return null;
  })();
  const chain = (angle === 'former' && probeSets.length > 0)
    ? buildChain(probeSets.map((ps, i) => ({
        index: i,
        titles: ps.pastJobTitles || [],
        count: (probeResults[i] || {}).totalElements ?? 0,
        enabled: groupEnabled[i] ?? true,
      })))
    : null;
  const uniqueTotal = (angle === 'former' && chain && uniqueCounts && !uniqueNeedsUpdate && !uniqueCounts.some(c => c === null))
    ? chain.reduce((s, cs, k) => {
        const uc = uniqueCounts[k] ?? 0;
        return s + (cs.overLimit ? Math.min(uc, 2500) : uc);
      }, 0)
    : null;
  const totalFetch = angle === 'former'
    ? (chain && uniqueCounts && !uniqueNeedsUpdate && !uniqueCounts.some(c => c === null)
        ? chain.reduce((s, cs, k) => s + roundMaxItems(uniqueCounts[k] ?? 0), 0)
        : 0)
    : fetchCaps.reduce((s, v) => s + (Number(v) || 0), 0);
  const allGroupsOff = angle === 'former' && probeSets.length > 0 && probeSets.every((_, i) => !(groupEnabled[i] ?? true));
  const isFormerStale = angle === 'former' && (uniqueNeedsUpdate || allGroupsOff);
  const failedSearches = (angle === 'former' && chain && uniqueCounts)
    ? chain.reduce((acc, cs, k) => {
        if (uniqueCounts[k] === null) acc.push(cs.search);
        return acc;
      }, [])
    : [];
  const hasUniqueError = failedSearches.length > 0;
  const totalStaged = pullResults.reduce((s, r) => s + (r.staged || 0), 0);
  const totalDupes = pullResults.reduce((s, r) => s + (r.dupes || 0), 0);

  const successfulCounts = probeResults
    .map((r) => r.totalElements)
    .filter((v) => v != null && v > 0);
  const medianCount = (() => {
    if (!successfulCounts.length) return 0;
    const s = [...successfulCounts].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 === 0 ? (s[m - 1] + s[m]) / 2 : s[m];
  })();

  // ── Helpers ───────────────────────────────────────────────────────────────
  const parseTitles = (str) => str.split(",").map((t) => t.trim()).filter(Boolean);

  const buildRunLabel = (run) => {
    if (run.meta && run.meta.kind === 'pull') {
      const k = run.meta.search;
      const n = run.meta.of;
      const groups = (run.meta.groups || []).map(g => g + 1).join(', ');
      const startPage = run.start_page != null ? run.start_page : 1;
      const maxItems = run.max_items != null ? run.max_items : LI_PAGE_SIZE;
      const a = (startPage - 1) * LI_PAGE_SIZE + 1;
      const b = a + maxItems - 1;
      return `Search ${k} of ${n} · groups ${groups} · people ${a}–${b}`;
    }
    const startPage = run.start_page != null ? run.start_page : 1;
    const maxItems = run.max_items != null ? run.max_items : LI_PAGE_SIZE;
    const a = (startPage - 1) * LI_PAGE_SIZE + 1;
    const b = a + maxItems - 1;
    const terms = run.companies || [];
    const range = `people ${a}–${b}`;
    if (!terms.length) return range;
    const raw = terms[0];
    const suffix = terms.length > 1 ? ` +${terms.length - 1}` : '';
    let name;
    if (raw.includes('linkedin.com')) {
      const signoffMatch = signoffCompanies.find((c) => c.url === raw);
      if (signoffMatch) {
        name = signoffMatch.name;
      } else {
        const m = raw.match(/linkedin\.com\/company\/([^/]+)/);
        name = m ? m[1].replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : raw;
      }
    } else {
      name = raw;
    }
    return `${name}${suffix} · ${range}`;
  };

  const SENIORITY_WORDS = new Set([
    "vp", "svp", "evp", "avp", "vice", "president", "director", "senior", "sr",
    "manager", "head", "chief", "principal", "lead", "executive", "of", "the", "and",
  ]);
  const isSeniorityOnly = (title) => {
    const tokens = title.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.length > 0 && tokens.every((tok) => SENIORITY_WORDS.has(tok));
  };

  const seniorityOnlyByGroup = sets.map((s) =>
    parseTitles(s.titles).filter(isSeniorityOnly)
  );
  const hasSeniorityOnlyTitles = seniorityOnlyByGroup.some((g) => g.length > 0);

  const canCheckCounts =
    !!signoffTarget?.name &&
    unlinkedCount === 0 &&
    !dupCompanyPair &&
    !hasSeniorityOnlyTitles &&
    (angle === "customer"
      ? activeCompanies.length > 0 && validSets.length > 0
      : angle === "competitor"
      ? validSets.length > 0 && activeCompanies.length > 0
      : validSets.length > 0);

  const pollRun = async (runId) => {
    while (true) {
      await new Promise((r) => setTimeout(r, 10000));
      const res = await fetch(`${API}/apify-runs/${runId}/check`, { method: "POST", headers });
      const data = await res.json();
      if (data.status === "ingested" || data.status === "partial" || data.status === "failed") return data;
    }
  };

  const addTargetChip = () => {
    if (!targetInput.trim() || targetChip) return;
    setTargetChip({ name: targetInput.trim(), url: null });
    setTargetInput("");
  };

  const addCompanyChip = () => {
    if (!companyInput.trim()) return;
    setCompanyChips((p) => [...p, { name: companyInput.trim(), url: null }]);
    setCompanyInput("");
  };

  const addSfCompanyChip = () => {
    if (!sfCompanyInput.trim()) return;
    const newChip = { name: sfCompanyInput.trim(), url: null };
    const dup = signoffCompanies.find((c) => !c.struck && sameCompany(c, newChip));
    if (dup) {
      setAddDupWarning("Already in the list");
      setTimeout(() => setAddDupWarning(null), 3000);
      return;
    }
    setSignoffCompanies((p) => [...p, { ...newChip, proposed: false, struck: false }]);
    setSfCompanyInput("");
  };

  const resetAll = () => {
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (_) {}
    setStage("configure");
    setTargetChip(null);
    setTargetInput("");
    setCompanyChips([]);
    setCompanyInput("");
    setTitlesInput("");
    setContextInput("");
    setExcludeHr(true);
    setExcludeJunior(true);
    setError(null);
    setPlanWarnings([]);
    setBatchId(null);
    setProbeSets([]);
    setProbeResults([]);
    setFetchCaps([]);
    setGroupEnabled([]);
    setUniqueCounts(null);
    setUniqueNeedsUpdate(false);
    setUniqueCountsCache({});
    setCountsExpandedGroups(new Set());
    if (autoRecountTimerRef.current) { clearTimeout(autoRecountTimerRef.current); autoRecountTimerRef.current = null; }
    setPullResults([]);
    setProgressMsg("");
    autoRankCalledRef.current = false;
  };

  // When pull finishes with staged leads and a topic, open the batch for ranking once.
  useEffect(() => {
    if (stage !== 'done') return;
    const staged = pullResults.reduce((s, r) => s + (r.staged || 0), 0);
    if (staged === 0) return;
    if (!searchContext.trim()) return;
    if (!onOpenBatch || batchId == null) return;
    if (autoRankCalledRef.current) return;
    autoRankCalledRef.current = true;
    onOpenBatch(batchId, { rank: true, noExpand: true });
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // When SourcingBatches reports ranking done for our batch, reset to Configure.
  useEffect(() => {
    if (!rankStatus || rankStatus.batchId !== batchId) return;
    if (rankStatus.state === 'done' && stage === 'done') resetAll();
  }, [rankStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlePropose ─────────────────────────────────────────────────────────
  const handlePropose = async () => {
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (_) {}
    setError(null);
    setStage("proposing");
    try {
      const res = await fetch(`${API}/search-plans/propose`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          angle,
          context: contextInput.trim(),
          target: { name: targetChip.name, url: targetChip.url || null },
          companies: companyChips.map((c) => ({ name: c.name, url: c.url || null })),
          titles: parseTitles(titlesInput),
          exclude_hr: excludeHr,
          exclude_junior: excludeJunior,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setProposedPlan({ ...data, captured: "as-proposed" });
      const p = data.plan;
      setSignoffTarget({ name: p.target.name, url: p.target.url || null });
      setSignoffCompanies(
        (p.company_sets || []).map((c) => ({
          name: c.name,
          url: c.url || null,
          proposed: c.proposed,
          struck: false,
          source_url: c.source_url || null,
        }))
      );
      setSets(
        (p.title_sets || []).length > 0
          ? p.title_sets.map((ts) => ({ titles: ts.join(", ") }))
          : [{ titles: "" }]
      );
      setSearchContext(p.search_context || "");
      setSfExcludeHr(p.exclude_hr !== false);
      setSfExcludeJunior(p.exclude_junior !== false);
      setPlanWarnings(data.warnings || []);
      setSourcesOpen(false);
      setStage("signoff");
    } catch (e) {
      setError(e.message);
      setStage("configure");
    }
  };

  // ── handleProbe ───────────────────────────────────────────────────────────
  const handleProbe = async () => {
    setError(null);
    setUniqueCountsCache({});
    setCountsExpandedGroups(new Set());
    setStage("probing");
    setProbeResults([]);
    setProbeLive([]); // UI-only progress

    const titlesUnion = (() => {
      const seen = new Set();
      const out = [];
      for (const s of validSets) {
        for (const t of parseTitles(s.titles)) {
          const tl = t.toLowerCase();
          if (!seen.has(tl) && out.length < 50) { seen.add(tl); out.push(t); }
        }
      }
      return out;
    })();

    const snap =
      angle === "former"
        ? validSets.map((s) => ({
            label: parseTitles(s.titles)[0] || "Set",
            linked: !!signoffTarget?.url,
            pastJobTitles: parseTitles(s.titles),
            companyName: null,
            companyUrl: null,
            titlesUnion: null,
          }))
        : activeCompanies.map((c) => ({
            label: c.name,
            linked: !!c.url,
            pastJobTitles: null,
            companyName: c.name,
            companyUrl: c.url || null,
            titlesUnion,
          }));
    setProbeSets(snap);

    // Create batch
    let bid;
    try {
      const _signoffBody = {
        target: { name: signoffTarget?.name || '', url: signoffTarget?.url || '' },
        companies: activeCompanies.map(c => ({ name: c.name, url: c.url || '', proposed: c.proposed || false })),
        role_groups: validSets.map(s => parseTitles(s.titles)).filter(g => g.length > 0),
        search_context: searchContext.trim(),
        exclude_hr: sfExcludeHr,
        exclude_junior: sfExcludeJunior,
      };
      const bRes = await fetch(`${API}/sourcing-batches`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          label: `${ANGLE_LABELS[angle]} — ${signoffTarget?.name || ""}`,
          angle,
          target_company: signoffTarget?.name || "",
          ...(searchContext.trim() ? { search_context: searchContext.trim() } : {}),
          ...(proposedPlan !== null ? { plan: proposedPlan } : {}),
          signoff: _signoffBody,
        }),
      });
      const bData = await bRes.json();
      if (!bRes.ok) throw new Error(bData.error || `HTTP ${bRes.status}`);
      bid = bData.id;
      setBatchId(bid);
      if (onIngested) onIngested();
    } catch (e) {
      setError(e.message);
      setStage("signoff");
      return;
    }

    // Probe each set sequentially
    const results = [];
    for (let i = 0; i < snap.length; i++) {
      const ps = snap[i];
      setProgressMsg(`Counting ${i + 1}/${snap.length}: ${ps.label}…`);
      try {
        const runBody = {
          max_items: 1,
          exclude_hr: sfExcludeHr,
          exclude_junior: sfExcludeJunior,
        };
        if (angle === "former") {
          runBody.past_job_titles = ps.pastJobTitles;
          runBody.meta = { kind: "group_count", groups: [i] };
          if (signoffTarget?.url) runBody.company_url = signoffTarget.url;
        } else {
          runBody.company_name = ps.companyName;
          if (ps.companyUrl) runBody.company_url = ps.companyUrl;
          if (ps.titlesUnion?.length) runBody.titles = ps.titlesUnion;
          if (signoffTarget?.url) runBody.target_url = signoffTarget.url;
        }
        const rRes = await fetch(`${API}/sourcing-batches/${bid}/apify-runs`, {
          method: "POST",
          headers,
          body: JSON.stringify(runBody),
        });
        const rData = await rRes.json();
        if (!rRes.ok) throw new Error(rData.error || `HTTP ${rRes.status}`);
        const terminal = await pollRun(rData.id);
        results.push({
          totalElements: terminal.total_elements ?? null,
          error: terminal.status === "failed" ? terminal.error || "Run failed" : null,
        });
      } catch (e) {
        results.push({ totalElements: null, error: e.message });
      }
      setProbeLive(results.slice()); // UI-only progress
    }

    setProbeResults(results);
    const enabledArr = new Array(results.length).fill(true);
    setGroupEnabled(enabledArr);

    if (angle === "former") {
      // Build chain and run unique probes in parallel.
      const builtChain = buildChain(snap.map((ps, i) => ({
        index: i,
        titles: ps.pastJobTitles || [],
        count: (results[i] || {}).totalElements ?? 0,
        enabled: true,
      })));
      setProbingUnique(true);
      const uCounts = new Array(builtChain.length).fill(null);
      await Promise.allSettled(builtChain.map(async (cs, k) => {
        // Single-group search with no exclusion: skip probe, reuse group count.
        if (cs.groups.length === 1 && cs.exclude.length === 0) {
          uCounts[k] = (results[cs.groups[0]] || {}).totalElements ?? 0;
          return;
        }
        try {
          const uBody = {
            max_items: 1,
            past_job_titles: cs.titles,
            exclude_hr: sfExcludeHr,
            exclude_junior: sfExcludeJunior,
            meta: { kind: "unique_count", search: cs.search, groups: cs.groups },
          };
          if (cs.exclude.length > 0) uBody.exclude_past_job_titles = cs.exclude;
          if (signoffTarget?.url) uBody.company_url = signoffTarget.url;
          const rRes = await fetch(`${API}/sourcing-batches/${bid}/apify-runs`, {
            method: "POST", headers, body: JSON.stringify(uBody),
          });
          const rData = await rRes.json();
          if (!rRes.ok) throw new Error(rData.error || `HTTP ${rRes.status}`);
          const terminal = await pollRun(rData.id);
          uCounts[k] = terminal.total_elements ?? 0;
        } catch (_) {
          uCounts[k] = null;
        }
      }));
      setUniqueCounts(uCounts);
      // Seed V3 cache with initial probe results.
      const seedCache = {};
      builtChain.forEach((cs, k) => { if (uCounts[k] !== null) seedCache[makeCacheKey(cs)] = uCounts[k]; });
      setUniqueCountsCache(seedCache);
      setProbingUnique(false);
    } else {
      setFetchCaps(results.map((r) => r.totalElements != null ? Math.min(r.totalElements, 2500) : 0));
    }

    setProgressMsg("");
    setStage("counts");
  };

  // ── handleUpdateUnique ────────────────────────────────────────────────────
  const handleUpdateUnique = async () => {
    if (autoRecountTimerRef.current) { clearTimeout(autoRecountTimerRef.current); autoRecountTimerRef.current = null; }
    setUniqueUpdating(true);
    const currentChain = buildChain(probeSets.map((ps, i) => ({
      index: i,
      titles: ps.pastJobTitles || [],
      count: (probeResults[i] || {}).totalElements ?? 0,
      enabled: groupEnabled[i] ?? true,
    })));
    const uCounts = new Array(currentChain.length).fill(null);
    const newCacheEntries = {};
    await Promise.allSettled(currentChain.map(async (cs, k) => {
      if (cs.groups.length === 1 && cs.exclude.length === 0) {
        uCounts[k] = (probeResults[cs.groups[0]] || {}).totalElements ?? 0;
        newCacheEntries[makeCacheKey(cs)] = uCounts[k];
        return;
      }
      const cacheKey = makeCacheKey(cs);
      if (uniqueCountsCache[cacheKey] !== undefined) {
        uCounts[k] = uniqueCountsCache[cacheKey];
        return;
      }
      try {
        const uBody = {
          max_items: 1,
          past_job_titles: cs.titles,
          exclude_hr: sfExcludeHr,
          exclude_junior: sfExcludeJunior,
          meta: { kind: "unique_count", search: cs.search, groups: cs.groups },
        };
        if (cs.exclude.length > 0) uBody.exclude_past_job_titles = cs.exclude;
        if (signoffTarget?.url) uBody.company_url = signoffTarget.url;
        const rRes = await fetch(`${API}/sourcing-batches/${batchId}/apify-runs`, {
          method: "POST", headers, body: JSON.stringify(uBody),
        });
        const rData = await rRes.json();
        if (!rRes.ok) throw new Error(rData.error || `HTTP ${rRes.status}`);
        const terminal = await pollRun(rData.id);
        uCounts[k] = terminal.total_elements ?? 0;
        newCacheEntries[cacheKey] = uCounts[k];
      } catch (_) {
        uCounts[k] = null;
      }
    }));
    setUniqueCounts(uCounts);
    setUniqueCountsCache(prev => ({ ...prev, ...newCacheEntries }));
    setUniqueNeedsUpdate(false);
    setUniqueUpdating(false);
  };

  // ── handleBackToPlan ──────────────────────────────────────────────────────
  const handleBackToPlan = () => {
    if (autoRecountTimerRef.current) { clearTimeout(autoRecountTimerRef.current); autoRecountTimerRef.current = null; }
    setProbeSets([]);
    setProbeResults([]);
    setFetchCaps([]);
    setGroupEnabled([]);
    setUniqueCounts(null);
    setUniqueNeedsUpdate(false);
    setUniqueCountsCache({});
    setCountsExpandedGroups(new Set());
    setTightenBanner(null);
    setTightenError(null);
    setStage("signoff");
  };

  // ── handleTighten ─────────────────────────────────────────────────────────
  const handleTighten = async () => {
    setTightenError(null);
    setTightenLoading(true);
    const prevSets = sets;
    const roleGroups = validSets.map((s) => parseTitles(s.titles));
    const countPayload = probeSets.map((ps, i) => ({
      company: ps.label,
      people_found: (probeResults[i] || {}).totalElements ?? null,
    }));
    try {
      const resp = await fetch(`${API}/search-plans/tighten`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          angle,
          target_name: signoffTarget?.name || "",
          search_context: searchContext,
          role_groups: roleGroups,
          counts: countPayload,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setTightenError(data.error || `Error ${resp.status}`);
        return;
      }
      const newSets = (data.role_groups || []).map((g) => ({ titles: g.join(", ") }));
      setSets(newSets);
      const removed = data.removed || [];
      const first8 = removed.slice(0, 8).join(", ");
      const ellipsis = removed.length > 8 ? "…" : "";
      setTightenBanner({
        text: `Tightened — removed ${removed.length} title${removed.length === 1 ? "" : "s"}: ${first8}${ellipsis}`,
        prevSets,
      });
      setUniqueCountsCache({});
      setCountsExpandedGroups(new Set());
      setStage("signoff");
    } catch (e) {
      setTightenError(e.message || "Request failed");
    } finally {
      setTightenLoading(false);
    }
  };

  // ── handlePull ────────────────────────────────────────────────────────────
  const handlePull = async () => {
    setError(null);
    if (angle === "former" && uniqueCounts && uniqueCounts.some(c => c === null)) return;

    // Build per-page params; skip pages with cap 0
    let pages;
    if (angle === "former") {
      const currentChain = buildChain(probeSets.map((ps, i) => ({
        index: i,
        titles: ps.pastJobTitles || [],
        count: (probeResults[i] || {}).totalElements ?? 0,
        enabled: groupEnabled[i] ?? true,
      })));
      pages = currentChain.map((cs, k) => {
        const maxItems = roundMaxItems((uniqueCounts && uniqueCounts[k]) ?? 0);
        if (!maxItems) return null;
        const page = {
          max_items: maxItems,
          past_job_titles: cs.titles,
          exclude_hr: sfExcludeHr,
          exclude_junior: sfExcludeJunior,
          meta: { kind: "pull", search: cs.search, of: currentChain.length, groups: cs.groups },
        };
        if (cs.exclude.length > 0) page.exclude_past_job_titles = cs.exclude;
        if (signoffTarget?.url) page.company_url = signoffTarget.url;
        return page;
      }).filter(Boolean);
    } else {
      pages = probeSets.map((ps, i) => {
        const cap = Number(fetchCaps[i]);
        if (!cap) return null;
        const page = { max_items: Math.min(2500, cap), exclude_hr: sfExcludeHr, exclude_junior: sfExcludeJunior };
        page.company_name = ps.companyName;
        if (ps.companyUrl) page.company_url = ps.companyUrl;
        if (ps.titlesUnion?.length) page.titles = ps.titlesUnion;
        if (signoffTarget?.url) page.target_url = signoffTarget.url;
        return page;
      }).filter(Boolean);
    }

    // Pre-check credits before creating any rows
    try {
      const cr = await fetch(`${API}/credits/me`, { headers });
      if (cr.ok) {
        const crData = await cr.json();
        if (typeof crData.balance === 'number') {
          setCreditsMe(crData);
          const cpp = crData.credits_per_profile ?? 1;
          const needed = totalFetch * cpp;
          if (crData.balance < needed) {
            return;
          }
        }
      }
    } catch (_) {}

    setStage("pulling");
    setPullResults([]);
    setPullRuns([]);
    setPullLive({}); // UI-only progress

    // Create all pending rows and submit first wave in one request
    let runIds;
    try {
      setProgressMsg(`Submitting ${pages.length} page${pages.length !== 1 ? "s" : ""}…`);
      const bRes = await fetch(`${API}/sourcing-batches/${batchId}/apify-runs/batch`, {
        method: "POST",
        headers,
        body: JSON.stringify({ pages }),
      });
      window.dispatchEvent(new Event('rasp:credits-changed'));
      const bData = await bRes.json();
      if (bRes.status === 402) {
        setCreditsMe((prev) => prev ? { ...prev, balance: bData.available ?? prev.balance } : prev);
        setStage("counts");
        return;
      }
      if (!bRes.ok) throw new Error(bData.error || `HTTP ${bRes.status}`);
      runIds = bData.run_ids;
    } catch (e) {
      setError(e.message);
      setStage("done");
      return;
    }

    // Fetch run data for labels (start_page, companies, max_items); order by runIds position
    try {
      const rRes = await fetch(`${API}/sourcing-batches/${batchId}/apify-runs`, { headers });
      if (rRes.ok) {
        const runsData = await rRes.json();
        const runMap = {};
        runsData.forEach((r) => { runMap[r.id] = r; });
        setPullRuns(runIds.map((id) => runMap[id] || null));
      }
    } catch (_) {}

    // Poll all runs concurrently; collect results in page order
    setProgressMsg(`Waiting for ${runIds.length} page${runIds.length !== 1 ? "s" : ""}…`);
    const resultArr = new Array(runIds.length).fill(null).map(() => ({ staged: 0, dupes: 0, error: null }));
    await Promise.allSettled(
      runIds.map(async (runId, i) => {
        try {
          const terminal = await pollRun(runId);
          resultArr[i] = {
            staged: terminal.staged ?? 0,
            dupes: terminal.intra_batch_dupes ?? 0,
            error: (terminal.status === "failed") ? terminal.error || "Run failed" : null,
          };
        } catch (e) {
          resultArr[i] = { staged: 0, dupes: 0, error: e.message };
        }
        setPullLive((p) => ({ ...p, [i]: resultArr[i] })); // UI-only progress
      })
    );

    setPullResults(resultArr);
    setProgressMsg("");
    setStage("done");
    if (onIngested) onIngested();
  };

  // ── Presentation (Source flow D) ──────────────────────────────────────────
  // Everything below reads existing state; the stage machine above is unchanged.
  // `shownStage` lags `stage` by COMPLETE_HOLD_MS after an async stage succeeds.
  const step = STEP_OF[shownStage];
  const elapsed = Math.max(0, now - stageStartRef.current);
  const doneCancelled = shownStage === "done" && pullResults.length === 0;
  const [stageTitle, stageSub] = doneCancelled
    ? ["Search stopped", "Your probe leads are saved in the batch below."]
    : STAGE_COPY[shownStage];
  // Header crumb: "/ Meta · former employees" once a company is chosen.
  const crumbName = (shownStage === "configure" || shownStage === "proposing")
    ? targetChip?.name
    : (signoffTarget?.name || targetChip?.name);
  const batchLabel = `${ANGLE_LABELS[angle]} — ${signoffTarget?.name || ""}`;
  const expectProposing = (angle === 'competitor' || angle === 'customer') ? 45000 : EXPECT_MS.proposing;
  const unit = angle === "former" ? "group" : "company";
  const units = angle === "former" ? "groups" : "companies";
  const secsLeft = (ms) => `About ${Math.max(1, Math.ceil(ms / 1000))}s left`;
  const proposedTitleStrings = new Set(
    ((proposedPlan && proposedPlan.plan && proposedPlan.plan.title_sets) || []).map((ts) => ts.join(", "))
  );

  // ── Loading screen data (proposing / probing / pulling) ──
  const loading = (() => {
    if (shownStage === "proposing") {
      const idx = holding ? 4 : Math.min(3, Math.floor(elapsed / (expectProposing / 4)));
      const plan = holding && proposedPlan ? proposedPlan.plan : null;
      const words = wordCount(contextInput);
      const titleCount = plan
        ? new Set((plan.title_sets || []).flat().map((t) => String(t).toLowerCase())).size
        : null;
      const groupCount = plan
        ? (angle === "former" ? (plan.title_sets || []).length : (plan.company_sets || []).length)
        : null;
      const linked = plan ? !!(plan.target && plan.target.url) : null;
      const defs = [
        ["Read your topic", words ? plural(words, "word") : "no topic"],
        ["Find the roles that matter", plan ? plural(titleCount, "title") : ""],
        angle === "former"
          ? ["Group job titles", plan ? plural(groupCount, "group") : ""]
          : ["Pick the companies to search", plan ? plural(groupCount, "company", "companies") : ""],
        ["Check the company link", plan ? (linked ? "linked" : "name match") : ""],
      ];
      const company = targetChip ? targetChip.name : "the company";
      const lines = [
        words ? "Reading what you wrote…" : `Looking at ${company}…`,
        `Figuring out which roles matter at ${company}…`,
        angle === "former" ? "Grouping job titles into sets…" : "Picking the companies to search…",
        "Checking the company link…",
      ];
      const left = expectProposing - elapsed;
      return {
        title: `Putting together a plan for ${company}`,
        status: holding ? "Your plan is ready" : lines[idx],
        reassurance: "Nothing is being fetched yet — you get to review this first.",
        progress: holding ? 1 : Math.min(0.92, elapsed / expectProposing),
        rows: defs.map(([label, value], k) => ({
          label,
          state: k < idx ? "done" : k === idx ? "active" : "todo",
          value: k < idx ? value : "",
          valueColor: value === "name match" ? "var(--warning)" : undefined,
        })),
        eta: holding ? "Done" : left > 0 ? secsLeft(left) : "Almost there…",
      };
    }
    if (shownStage === "probing") {
      const n = probeSets.length;
      const doneN = holding ? n : probeLive.length;
      const cur = holding || doneN >= n ? 0 : Math.min(0.9, (now - probeStepStartRef.current) / EXPECT_MS.probeSet);
      const left = (n - doneN) * EXPECT_MS.probeSet - (now - probeStepStartRef.current);
      const uniqueActive = angle === 'former' && doneN >= n && !holding && uniqueCounts === null;
      const uniqueDoneForLoading = angle === 'former' && (uniqueCounts !== null || holding);
      const totalSteps = angle === 'former' ? n + 1 : n;
      const uniqueProgress = angle === 'former' ? (uniqueDoneForLoading ? 1 : (uniqueActive ? 0.5 : 0)) : 0;
      const groupRows = probeSets.map((ps, i) => {
        const r = probeLive[i] || (holding ? probeResults[i] : null);
        return {
          label: ps.pastJobTitles ? ps.pastJobTitles.join(", ") : ps.label,
          state: r ? "done" : i === doneN ? "active" : "todo",
          value: r ? (r.error ? "error" : fmtN(r.totalElements)) : "",
          valueColor: r && r.error ? "var(--negative)" : undefined,
        };
      });
      const rows = angle === 'former'
        ? [...groupRows, {
            label: "Counting unique people…",
            state: uniqueDoneForLoading ? "done" : (doneN >= n ? "active" : "todo"),
            value: "",
          }]
        : groupRows;
      return {
        title: "Checking how many people match",
        status: uniqueActive
          ? "Counting unique people…"
          : doneN >= n ? `All ${units} counted` : `Counting ${unit} ${doneN + 1} of ${n}…`,
        reassurance: `We look at one profile per ${unit} — just enough to count.`,
        progress: totalSteps ? (doneN + cur + uniqueProgress) / totalSteps : 0,
        rows,
        eta: doneN >= n ? "Done" : left > 0 ? secsLeft(left) : "Almost there…",
      };
    }
    if (shownStage === "pulling") {
      const formerPages = (angle === 'former' && chain && uniqueCounts)
        ? chain.filter((cs, k) => roundMaxItems(uniqueCounts[k] ?? 0) > 0)
        : null;
      const n = formerPages ? formerPages.length : probeSets.length;
      const live = { ...pullLive };
      if (holding) pullResults.forEach((r, i) => { if (!live[i]) live[i] = r; });
      const doneN = Object.keys(live).length;
      const inflight = Math.min(0.9, elapsed / EXPECT_MS.pullRun);
      const staged = Object.values(live).reduce((s, r) => s + (r.staged || 0), 0);
      const left = EXPECT_MS.pullRun - elapsed;
      const starting = doneN === 0 && progressMsg.startsWith("Submitting");
      return {
        title: "Bringing in your leads",
        status: starting ? `Starting ${plural(n, "run")}…` : `${fmtN(staged)} leads in so far`,
        reassurance: "Feel free to leave this page — the batch keeps filling in the background.",
        progress: holding ? 1 : n ? (doneN + (n - doneN) * inflight) / n : 0,
        rows: formerPages
          ? formerPages.map((cs, k) => {
              const r = live[k];
              const label = `Search ${cs.search} · groups ${cs.groups.map(g => g + 1).join(', ')}`;
              return {
                label,
                state: r ? "done" : "active",
                value: r ? (r.error ? "failed" : fmtN(r.staged)) : "",
                valueColor: r && r.error ? "var(--negative)" : undefined,
              };
            })
          : probeSets.map((ps, i) => {
              const r = live[i];
              return {
                label: ps.pastJobTitles ? ps.pastJobTitles.join(", ") : ps.label,
                state: r ? "done" : "active",
                value: r ? (r.error ? "failed" : fmtN(r.staged)) : "",
                valueColor: r && r.error ? "var(--negative)" : undefined,
              };
            }),
        eta: holding || doneN >= n
          ? "Done"
          : left > 0
          ? secsLeft(left)
          : elapsed > EXPECT_MS.pullRun * 2
          ? "Taking longer than usual — still running"
          : "Almost there…",
      };
    }
    return null;
  })();

  // ── Counts data ──
  const counts = probeSets.map((_, i) => (probeResults[i] || {}).totalElements);
  const totalMatch = counts.reduce((s, c) => s + (c || 0), 0);
  const maxCount = Math.max(1, ...counts.map((c) => c || 0));
  const failedCounts = probeResults.filter((r) => r && r.error).length;
  const setCap = (i, v) => setFetchCaps((p) => p.map((x, j) => (j === i ? v : x)));

  // ── Shared styles ──
  const eyebrow = { fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 500, letterSpacing: ".08em", color: "var(--text-4)", textTransform: "uppercase" };
  const checkLabel = { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-2)", cursor: "pointer", whiteSpace: "nowrap" };
  const checkInput = { accentColor: "var(--accent)", width: 14, height: 14, margin: 0 };
  // One stage = one flat column of sheet + rows, flush with the page header.
  const stageBody = { display: "flex", flexDirection: "column", gap: 14, animation: "nsp-slideIn .4s cubic-bezier(.2,.7,.2,1)" };
  const primaryBtn = { height: 34, padding: "0 18px", fontWeight: 600 };
  // Configure sheet rows: label column + field column.
  const sheetRow2 = { display: "grid", gridTemplateColumns: "180px minmax(0, 1fr)", gap: 12, alignItems: "start", padding: "16px 18px", borderBottom: "1px solid var(--line-soft)" };
  const rowLabel = { fontSize: 13, fontWeight: 500, color: "var(--text-1)", paddingTop: 10 };
  const caption = { display: "block", fontSize: 11, fontWeight: 400, color: "var(--text-4)", marginTop: 2 };
  // Sign-off / counts grids (handoff: scroll inside the sheet at narrow widths).
  const SIGNOFF_COLS = "36px 220px minmax(0, 1fr) 90px";
  const COUNTS_COLS = "36px 220px minmax(0, 1fr) 110px 120px 70px";
  const rowNum = (i) => <span style={{ fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 500, color: "var(--accent)" }}>{String(i + 1).padStart(2, "0")}</span>;
  const bannerDot = (c) => <span style={{ width: 6, height: 6, borderRadius: "50%", background: c, display: "block", flex: "none" }} />;
  const tealBannerRow = {
    display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--line)", fontSize: 12, color: "var(--text-2)",
    background: "linear-gradient(90deg, rgba(14,116,144,.25), rgba(45,212,191,.04))",
  };
  const noteRow = { padding: "8px 16px", borderBottom: "1px solid var(--line-soft)", fontSize: 12 };
  // Context row shared by sign-off and counts: COMPANY · chip · TOPIC · text.
  const contextRow = (withEdit) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span style={{ ...eyebrow, width: 72 }}>Company</span>
      {signoffTarget && (
        <Chip
          tone="target"
          chip={signoffTarget}
          onUpdate={(c) => setSignoffTarget(c)}
          onRemove={() => setSignoffTarget(null)}
        />
      )}
      {signoffTarget && !signoffTarget.url && (
        <span data-testid="nsp-link-hint" style={{ fontSize: 11, color: "var(--warning)" }}>
          LinkedIn link required
        </span>
      )}
      <span style={{ ...eyebrow, marginLeft: 14 }}>Topic</span>
      <span style={{ flex: "1 1 320px", minWidth: 0, fontSize: 13, color: searchContext.trim() ? "var(--text-2)" : "var(--text-4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={searchContext}>
        {searchContext.trim() || "No topic yet — add one and every lead gets ranked against it"}
      </span>
      {withEdit && (
        <button
          type="button"
          onClick={() => setTopicOpen((v) => !v)}
          style={{ fontSize: 12, background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--accent)" }}
        >
          {topicOpen ? "Done" : "Edit"}
        </button>
      )}
    </div>
  );

  const excludeChecks = (hr, setHr, jr, setJr) => (
    <>
      <label style={checkLabel}>
        <input type="checkbox" checked={hr} onChange={(e) => setHr(e.target.checked)} style={checkInput} />
        Exclude HR / recruiting
      </label>
      <label style={checkLabel}>
        <input type="checkbox" checked={jr} onChange={(e) => setJr(e.target.checked)} style={checkInput} />
        Exclude entry-level
      </label>
    </>
  );

  const proposeHint = !targetChip
    ? "Add a company to continue"
    : angle === "customer" && !contextInput.trim()
    ? "Describe who you're looking for to continue"
    : "Nothing is fetched yet — you'll review the plan first";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="nsp-panel" data-stage={shownStage}>
      {/* ── Page header: h1 + stage line on the left, stepper on the right ── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div key={shownStage + (doneCancelled ? "-x" : "")} style={{ minWidth: 0, flex: "1 1 320px", animation: "nsp-countUp .35s ease-out" }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.01em", color: "var(--text-1)", margin: "0 0 4px" }}>
            Source
            {crumbName && (
              <span style={{ color: "var(--text-4)", fontWeight: 400 }}> / {crumbName} · {CRUMB_ANGLE[angle]}</span>
            )}
          </h1>
          <p style={{ fontSize: 13, color: "var(--text-3)", margin: 0, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span>
              <span style={{ color: "var(--text-2)", fontWeight: 500 }}>{stageTitle}</span>
              <span> — {stageSub}</span>
            </span>
            {shownStage === "configure" && (
              <button
                type="button"
                className="nsp-help"
                aria-expanded={showHelp}
                onClick={() => setShowHelp((v) => !v)}
                style={{ height: 24, padding: "0 10px", fontSize: 11 }}
              >
                <span className="nsp-help-q">?</span>
                How does this work
              </button>
            )}
          </p>
        </div>
        <HeaderStepper step={step} meta={STAGE_META[shownStage]} />
      </div>

      {shownStage === "configure" && showHelp && (
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12,
          padding: "14px 16px", border: "1px solid var(--line-soft)", borderRadius: 10, background: "var(--subbar-bg)",
          animation: "nsp-popIn .25s ease-out",
        }}>
          {[
            ["01", "Draft a plan", "Rasp drafts the companies, job title groups and topic for you to review."],
            ["02", "Count matches", "A small check per group shows how many people match; you choose how many to bring in."],
            ["03", "Bring in and rank", "Leads land in a batch below; rank them there (and score, for formers)."],
          ].map(([num, t, desc]) => (
            <div key={num} style={{ display: "flex", gap: 8 }}>
              <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--accent)", flexShrink: 0, marginTop: 1 }}>{num}</span>
              <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
                <strong style={{ color: "var(--text-1)", fontWeight: 500 }}>{t}.</strong> {desc}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && !busy && (
        <div style={{
          padding: "8px 12px", borderRadius: 8, fontSize: 12, color: "var(--negative)",
          border: "1px solid rgba(224,106,90,.32)", background: "rgba(224,106,90,.08)", display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ fontSize: 11, background: "none", border: "none", color: "var(--negative)", cursor: "pointer", textDecoration: "underline" }}
          >
            dismiss
          </button>
        </div>
      )}

      {/* ── CONFIGURE: one sheet, label column + field column ── */}
      {shownStage === "configure" && (
        <div style={stageBody}>
          <div className="nsp-sheet">
            {/* Who */}
            <div style={sheetRow2}>
              <span style={{ ...rowLabel, paddingTop: 6 }}>Who are you looking for?</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                <div className="nsp-seg">
                  {["former", "competitor", "customer"].map((a) => (
                    <button key={a} type="button" onClick={() => setAngle(a)} className={angle === a ? "nsp-seg-on" : ""}>
                      {ANGLE_LABELS[a]}
                    </button>
                  ))}
                </div>
                <span style={{ fontSize: 12, color: "var(--text-4)" }}>{ANGLE_HINT}</span>
              </div>
            </div>

            {/* Company + titles */}
            <div style={sheetRow2}>
              <span style={{ ...rowLabel, display: "flex", alignItems: "center", gap: 6 }}>
                Which company?
                {targetChip && <span data-testid="nsp-target-dot" style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--positive)", display: "block" }} />}
              </span>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div
                  className="nsp-field"
                  style={{ flex: "1 1 260px", minHeight: 40, padding: "3px 6px 3px 12px", gap: 6, flexWrap: "wrap", ...(targetChip ? { borderColor: "rgba(45,212,191,.3)" } : {}) }}
                >
                  {targetChip ? (
                    <Chip
                      tone="committed"
                      chip={targetChip}
                      onUpdate={(c) => setTargetChip(c)}
                      onRemove={() => setTargetChip(null)}
                      onClear={() => setTargetChip(null)}
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder="Type a company and press Enter"
                      value={targetInput}
                      onChange={(e) => setTargetInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTargetChip(); } }}
                      style={{ height: 32, minWidth: 120 }}
                    />
                  )}
                </div>
                <label style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                  <span style={{ fontSize: 11, color: "var(--text-4)" }}>
                    {angle === "former"
                      ? "Any job titles in mind?"
                      : angle === "competitor"
                      ? "Any current role titles in mind?"
                      : "Any likely roles in mind?"}{" "}
                    <span style={{ color: "var(--text-5)" }}>Optional</span>
                  </span>
                  <span className="nsp-field" style={{ height: 40, padding: "0 12px" }}>
                    <input
                      type="text"
                      placeholder="e.g. Software Engineer, Senior SWE"
                      value={titlesInput}
                      onChange={(e) => setTitlesInput(e.target.value)}
                    />
                  </span>
                </label>
              </div>
            </div>

            {/* Companies group (competitor / customer) */}
            {angle !== "former" && (
              <div style={sheetRow2}>
                <span style={rowLabel}>
                  {angle === "competitor" ? "Competitor companies" : "Customer companies"}
                  {angle === "customer" && (
                    <span style={caption}>Optional — leave empty and the plan will propose candidates</span>
                  )}
                </span>
                <div className="nsp-field" style={{ minHeight: 40, padding: "3px 6px 3px 8px", gap: 6, flexWrap: "wrap" }}>
                  {companyChips.map((c, i) => (
                    <Chip
                      key={i}
                      chip={c}
                      onUpdate={(nc) => setCompanyChips((p) => p.map((x, j) => j === i ? nc : x))}
                      onRemove={() => setCompanyChips((p) => p.filter((_, j) => j !== i))}
                      warnMixed={anyConfigCompanyLinked && !c.url}
                    />
                  ))}
                  {companyChips.length < 10 && (
                    <input
                      type="text"
                      placeholder="Type a company and press Enter"
                      value={companyInput}
                      onChange={(e) => setCompanyInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCompanyChip(); } }}
                      style={{ height: 32, minWidth: 160, paddingLeft: 4 }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Context (last divided row — the footer sits directly under it) */}
            <div style={{ ...sheetRow2, borderBottom: "none" }}>
              <span style={rowLabel}>
                {angle === "customer" ? "Who are you looking for?" : "What should they speak to?"}
                <span style={caption}>
                  {angle === "customer" ? "Required" : "Optional, but it makes the ranking much better"}
                </span>
              </span>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                <textarea
                  className="nsp-area"
                  rows={3}
                  placeholder="Describe the business questions in a sentence or two"
                  value={contextInput}
                  onChange={(e) => setContextInput(e.target.value)}
                  style={{ minHeight: 84, fontSize: 13 }}
                />
                <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-5)", alignSelf: "flex-end", minHeight: 14 }}>
                  {contextInput.length ? `${contextInput.length} chars` : ""}
                </span>
              </label>
            </div>

            {/* Footer row — no divider above */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px 18px", flexWrap: "wrap", padding: "14px 18px" }}>
              {excludeChecks(excludeHr, setExcludeHr, excludeJunior, setExcludeJunior)}
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: "var(--text-4)" }}>{proposeHint}</span>
              <button className="r-btn r-btn-amber nsp-draft" onClick={handlePropose} disabled={!canPropose}>
                <span className="nsp-draft-dot" />
                Draft my plan
                <span className="nsp-draft-chev" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PROPOSING / PROBING / PULLING ── */}
      {loading && <LoadingScreen key={shownStage} {...loading} />}

      {/* ── SIGNOFF: context row · company row · the plan as one flat sheet ── */}
      {shownStage === "signoff" && (
        <div style={stageBody}>
          {contextRow(true)}
          {topicOpen && (
            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={eyebrow}>
                Your topic <span style={{ color: "var(--text-5)", textTransform: "none", letterSpacing: 0 }}>— we'll rank every lead against this</span>
              </span>
              <textarea
                className="nsp-area"
                rows={3}
                autoFocus
                value={searchContext}
                onChange={(e) => setSearchContext(e.target.value)}
                style={{ minHeight: 76, fontSize: 13, lineHeight: 1.5, padding: "8px 10px", borderRadius: 8 }}
              />
            </label>
          )}

          {/* Company chips (competitor / customer) */}
          {angle !== "former" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                <span style={{ ...eyebrow, width: 72, paddingTop: 7 }}>{angle === "competitor" ? "Competitors" : "Customers"}</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", flex: 1, minWidth: 0 }}>
                  {signoffCompanies.map((c, i) => (
                    <React.Fragment key={i}>
                      <Chip
                        chip={c}
                        proposed={c.proposed}
                        struck={c.struck}
                        warnMixed={anySignoffCompanyLinked && !c.url && !c.struck}
                        onStrike={() =>
                          setSignoffCompanies((p) => p.map((x, j) => j === i ? { ...x, struck: !x.struck } : x))
                        }
                        onUpdate={(nc) =>
                          setSignoffCompanies((p) => p.map((x, j) => j === i ? { ...x, name: nc.name, url: nc.url } : x))
                        }
                        onRemove={() =>
                          setSignoffCompanies((p) => p.filter((_, j) => j !== i))
                        }
                      />
                      {!c.url && !c.struck && (
                        <span data-testid="nsp-link-hint" style={{ fontSize: 11, color: "var(--warning)" }}>
                          LinkedIn link required
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                  {signoffCompanies.filter((c) => !c.struck).length < 10 && (
                    <span className="nsp-field" style={{ height: 30, padding: "0 10px", flex: "1 1 160px", minWidth: 160, maxWidth: 260 }}>
                      <input
                        type="text"
                        placeholder="Add company — press Enter"
                        value={sfCompanyInput}
                        onChange={(e) => setSfCompanyInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSfCompanyChip(); } }}
                        style={{ fontSize: 12 }}
                      />
                    </span>
                  )}
                  {planSourceCompanies.length > 0 && (
                    <button
                      data-testid="nsp-sources-btn"
                      style={{ fontSize: 11, height: 24, padding: "0 8px", border: "1px solid var(--line)", borderRadius: 6, background: "transparent", color: "var(--text-3)", cursor: "pointer" }}
                      onClick={() => setSourcesOpen((o) => !o)}
                    >
                      Sources
                    </button>
                  )}
                </div>
              </div>
              {sourcesOpen && planSourceCompanies.length > 0 && (
                <ul data-testid="nsp-sources-list" style={{ margin: 0, padding: "0 0 0 96px", listStyle: "disc", fontSize: 12, color: "var(--text-3)" }}>
                  {planSourceCompanies.map((c, i) => (
                    <li key={i} style={{ marginBottom: 3 }}>
                      {c.name}{" — "}
                      <a href={c.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
                        {extractDomain(c.source_url)}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {nameMatchCompanies.length > 0 && (
                <div style={{ fontSize: 11, color: "var(--text-3)", paddingLeft: 82 }}>
                  Matching by name (may include same-named companies):{" "}
                  {nameMatchCompanies.map((c) => c.name).join(", ")}.
                </div>
              )}
              {addDupWarning && (
                <div data-testid="nsp-dup-warning" style={{ fontSize: 11, color: "var(--warning)", paddingLeft: 82 }}>{addDupWarning}</div>
              )}
            </div>
          )}

          {/* The plan sheet */}
          <div className="nsp-sheet">
            {/* Banner row */}
            <div style={tealBannerRow}>
              {bannerDot("var(--accent)")}
              <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                {angle === "former" ? (
                  <>Here's the plan. We grouped the job titles into <Mono>{validSets.length}</Mono> {validSets.length === 1 ? "set" : "sets"} — edit or remove any of them, then we'll count how many people match.</>
                ) : (
                  <>Here's the plan. We'll search <Mono>{activeCompanies.length}</Mono> {angle === "competitor" ? "competitor" : "likely customer"} {activeCompanies.length === 1 ? "company" : "companies"} — edit anything, then we'll count how many people match.</>
                )}
              </span>
              <button
                type="button"
                data-testid="nsp-expand-all"
                onClick={() => {
                  const allExp = sets.every((_, j) => expandedGroups.has(j));
                  setExpandedGroups(allExp ? new Set() : new Set(sets.map((_, j) => j)));
                }}
                style={{ marginLeft: "auto", height: 24, padding: "0 8px", borderRadius: 6, border: "1px solid rgba(45,212,191,.3)", background: "transparent", color: "var(--accent)", fontSize: 11, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap", flex: "none" }}
              >
                {sets.every((_, j) => expandedGroups.has(j)) ? "Collapse all" : "Expand all"}
              </button>
            </div>

            {tightenBanner && (
              <div data-testid="tighten-banner" style={{ ...noteRow, display: "flex", alignItems: "flex-start", gap: 8, color: "var(--text-2)", background: "rgba(69,196,154,.08)" }}>
                <span style={{ flex: 1, minWidth: 0 }}>{tightenBanner.text}</span>
                <button
                  data-testid="tighten-undo"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontSize: 12, padding: 0, whiteSpace: "nowrap" }}
                  onClick={() => { setSets(tightenBanner.prevSets); setTightenBanner(null); }}
                >
                  Undo
                </button>
              </div>
            )}
            {signoffTarget && !signoffTarget.url && (
              <div style={{ ...noteRow, display: "flex", alignItems: "center", gap: 8, color: "var(--warning)" }}>
                {bannerDot("var(--warning)")}
                We matched the company by name. Link its LinkedIn page for an exact match (click the chip).
              </div>
            )}
            {planWarnings.length > 0 && (
              <div style={{ ...noteRow, fontSize: 11, color: "var(--warning)" }}>
                {planWarnings.map((w, i) => (
                  <div key={i} style={{ marginBottom: 2 }}>{w}</div>
                ))}
              </div>
            )}
            {proposedPlan?.plan?.notes && (
              <div style={{ ...noteRow, color: "var(--text-3)", lineHeight: 1.5 }}>
                <span style={{ ...eyebrow, marginRight: 8 }}>Why these sets</span>{proposedPlan.plan.notes}
              </div>
            )}

            <div className="nsp-sheet-scroll"><div style={{ minWidth: 820 }}>
              <div className="nsp-sheet-head nsp-sheet-grid" style={{ gridTemplateColumns: SIGNOFF_COLS }}>
                <span>#</span>
                <span>{angle === "former" ? "Group" : angle === "competitor" ? "Role group" : "Likely roles"}</span>
                <span>Titles</span>
                <span style={{ textAlign: "right" }}>Count</span>
              </div>
              {sets.map((s, i) => {
                const isProposed = proposedTitleStrings.has(s.titles);
                const isExpanded = expandedGroups.has(i);
                const titles = parseTitles(s.titles);
                const toggleExpand = () => setExpandedGroups(eg => {
                  const n = new Set(eg);
                  n.has(i) ? n.delete(i) : n.add(i);
                  return n;
                });
                const groupSeniorityTitles = seniorityOnlyByGroup[i] || [];
                return (
                  <div key={i} className="nsp-sheet-row" style={{ animation: `nsp-fadeUp .4s ease-out ${i * 0.07}s both` }}>
                    <div className="nsp-sheet-grid" style={{ gridTemplateColumns: SIGNOFF_COLS, minHeight: 36, ...(isExpanded ? { alignItems: "start" } : {}) }}>
                      {rowNum(i)}
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={titles[0] || undefined}>
                        {titles[0] || `Group ${i + 1}`}
                        {isProposed && (
                          <span style={{
                            display: "block", width: "max-content", marginTop: 3, fontFamily: "var(--font-data)", fontSize: 10, color: "var(--accent)",
                            border: "1px dashed rgba(45,212,191,.4)", borderRadius: 4, padding: "0 5px", lineHeight: "16px",
                          }}>proposed</span>
                        )}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        {isExpanded ? (
                          <span className="nsp-field" style={{ display: "flex", padding: "6px 10px", borderRadius: 8, alignItems: "flex-start" }}>
                            <textarea
                              data-testid={`role-group-textarea-${i}`}
                              ref={(el) => { if (el) autoSize(el); }}
                              placeholder="comma-separated titles"
                              value={s.titles}
                              onChange={(e) => {
                                setSets((p) => p.map((x, j) => j === i ? { ...x, titles: e.target.value } : x));
                                autoSize(e.target);
                              }}
                              style={{
                                fontSize: 13, width: "100%", border: "none", background: "none",
                                resize: "none", outline: "none", lineHeight: 1.5,
                                fontFamily: "inherit", color: "var(--text-1)",
                                overflow: "hidden",
                              }}
                            />
                          </span>
                        ) : titles.length === 0 ? (
                          <span className="nsp-field" style={{ height: 30, padding: "0 10px", borderRadius: 8, maxWidth: 420 }}>
                            <input
                              type="text"
                              placeholder="comma-separated titles"
                              value={s.titles}
                              onChange={(e) =>
                                setSets((p) => p.map((x, j) => j === i ? { ...x, titles: e.target.value } : x))
                              }
                              style={{ fontSize: 13 }}
                            />
                          </span>
                        ) : (
                          <TitleChips
                            titles={titles}
                            expanded={false}
                            onMore={toggleExpand}
                            moreTestId={`role-group-more-${i}`}
                          />
                        )}
                      </div>
                      <span style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2 }}>
                        <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)", marginRight: 4 }}>{titles.length}</span>
                        <button
                          type="button"
                          data-testid={`role-group-expand-${i}`}
                          onClick={toggleExpand}
                          title={isExpanded ? "Collapse" : "Expand all titles"}
                          style={{ fontSize: 13, color: "var(--text-4)", background: "none", border: "none", padding: "2px", cursor: "pointer", flex: "none" }}
                        >{isExpanded ? "⊟" : "⊞"}</button>
                        {(sets.length > 1 || angle === "customer") ? (
                          <button
                            type="button"
                            className="nsp-x"
                            onClick={() => setSets((p) => p.filter((_, j) => j !== i))}
                            title="Remove group"
                            aria-label={`Remove group ${i + 1}`}
                          >×</button>
                        ) : (
                          <span style={{ width: 24, flex: "none" }} />
                        )}
                      </span>
                    </div>
                    {groupSeniorityTitles.map((bad) => (
                      <div
                        key={bad}
                        data-testid="nsp-seniority-warning"
                        style={{ fontSize: 12, color: "var(--warning)", paddingLeft: 36, marginTop: 4 }}
                      >
                        &ldquo;{bad}&rdquo; on its own matches every {bad} — combine it with a function, or remove it
                      </div>
                    ))}
                  </div>
                );
              })}
              {/* Footer row */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", flexWrap: "wrap" }}>
                {sets.length < maxSets && (
                  <button
                    type="button"
                    data-testid="nsp-add-group-btn"
                    className="nsp-add"
                    style={{ marginLeft: 0, height: 26, borderRadius: 8 }}
                    onClick={() => {
                      const newIdx = sets.length;
                      setSets((p) => [...p, { titles: "" }]);
                      setExpandedGroups((prev) => new Set([...prev, newIdx]));
                    }}
                  >
                    + Add group
                  </button>
                )}
                <span style={{ fontSize: 12, color: "var(--text-4)" }}>
                  {angle === "former"
                    ? `Each set is one LinkedIn search, kept under the 2,500-person limit. ${sets.length} of ${maxSets} groups.`
                    : `Each ${unit} is counted on its own — ${runsCount} ${runsCount === 1 ? "run" : "runs"} total. ${sets.length} of ${maxSets} ${sets.length === 1 ? "group" : "groups"}.`}
                </span>
              </div>
            </div></div>
          </div>

          {dupCompanyPair && (
            <div data-testid="nsp-dup-pair" style={{ fontSize: 12, color: "var(--warning)" }}>
              Two companies have the same LinkedIn link: {dupCompanyPair[0]}, {dupCompanyPair[1]}.
            </div>
          )}
          {!dupCompanyPair && unlinkedCount > 0 && (
            <div data-testid="nsp-unlinked-msg" style={{ fontSize: 12, color: "var(--warning)" }}>
              Add a LinkedIn link to {unlinkedCount} {unlinkedCount === 1 ? "company" : "companies"} to continue.
            </div>
          )}
          {angle === "customer" && validSets.length === 0 && (
            <div data-testid="nsp-no-roles-msg" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "var(--warning)" }}>Add at least one likely role — Rasp only searches these roles at each customer.</span>
              {sets.length === 0 && (
                <button
                  type="button"
                  data-testid="nsp-add-roles-btn"
                  className="r-btn r-btn-ghost"
                  style={{ fontSize: 12 }}
                  onClick={() => setSets((p) => [...p, { titles: "" }])}
                >+ Add likely roles</button>
              )}
            </div>
          )}

          {/* Footer */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px 18px", flexWrap: "wrap" }}>
            {excludeChecks(sfExcludeHr, setSfExcludeHr, sfExcludeJunior, setSfExcludeJunior)}
            <div style={{ flex: 1 }} />
            <button onClick={resetAll} className="r-btn r-btn-ghost" style={{ border: "none", padding: "0 6px", color: "var(--text-4)" }}>Start over</button>
            <button onClick={() => setStage("configure")} className="r-btn r-btn-ghost">Back</button>
            <button
              className="r-btn r-btn-primary"
              onClick={handleProbe}
              disabled={!canCheckCounts}
              style={{ ...primaryBtn, ...(!canCheckCounts ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
            >
              Looks good, count matches
            </button>
          </div>
        </div>
      )}

      {/* ── COUNTS: context row · the counts sheet · summary strip ── */}
      {shownStage === "counts" && (
        <div style={stageBody}>
          {contextRow(false)}

          <div className="nsp-sheet">
            {angle === "former" && allGroupsOff && (
              <div data-testid="all-groups-off-msg" style={{ ...noteRow, fontSize: 13, color: "var(--text-4)" }}>
                Switch on at least one group.
              </div>
            )}
            {angle === "former" && hasUniqueError && !allGroupsOff && (
              <div data-testid="unique-error" style={{ ...noteRow, color: "var(--warning)" }}>
                {failedSearches.length === 1
                  ? `Couldn't count search ${failedSearches[0]}. Retry to continue.`
                  : `Couldn't count searches ${failedSearches.join(', ')}. Retry to continue.`
                }
              </div>
            )}
            {failedCounts > 0 && (
              <div style={{ ...noteRow, color: "var(--warning)" }}>
                {plural(failedCounts, unit, units)} couldn't be counted{angle === "former" ? "." : " — set a number to bring some in anyway."}
              </div>
            )}

            <div className="nsp-sheet-scroll"><div style={{ minWidth: 860 }}>
              <div className="nsp-sheet-head nsp-sheet-grid" style={{ gridTemplateColumns: COUNTS_COLS }}>
                <span>#</span>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span>{angle === "former" ? "Group" : "Company"}</span>
                  {angle === "former" && probeSets.length > 0 && (
                    <button
                      type="button"
                      data-testid="counts-expand-all"
                      onClick={() => {
                        const allExp = probeSets.every((_, j) => countsExpandedGroups.has(j));
                        setCountsExpandedGroups(allExp ? new Set() : new Set(probeSets.map((_, j) => j)));
                      }}
                      style={{ fontSize: 10, background: "none", border: "none", cursor: "pointer", color: "var(--accent)", textDecoration: "underline", padding: 0, fontWeight: 500, letterSpacing: 0, textTransform: "none", fontFamily: "inherit" }}
                    >
                      {probeSets.every((_, j) => countsExpandedGroups.has(j)) ? "Collapse all" : "Expand all"}
                    </button>
                  )}
                </span>
                <span>Titles</span>
                <span style={{ textAlign: "right" }}>Matches</span>
                <span style={{ textAlign: "right" }}>Bring in</span>
                <span style={{ textAlign: "right" }}>{angle === "former" ? "On" : ""}</span>
              </div>
              {probeSets.map((ps, i) => {
                const pr = probeResults[i] || {};
                const count = pr.totalElements;
                const showNudge =
                  angle !== "former" &&
                  !ps.linked &&
                  count != null &&
                  !pr.error &&
                  (count > 1000 || (medianCount > 0 && count > 3 * medianCount));
                const cap = Number(fetchCaps[i]) || 0;
                const titles = angle === "former" ? (ps.pastJobTitles || []) : (ps.titlesUnion || []);
                const isOverLimit = angle === "former" && count != null && count > 2500;
                const enabled = groupEnabled[i] ?? true;
                const isCountsExpanded = countsExpandedGroups.has(i);
                const matchColor = pr.error ? "var(--negative)" : (count === 0 || (count == null && !pr.error)) ? "var(--warning)" : "var(--text-1)";
                return (
                  <div key={i} className="nsp-sheet-row" style={{ ...(angle === "former" && !enabled ? { opacity: 0.55 } : {}) }}>
                    <div className="nsp-sheet-grid" style={{ gridTemplateColumns: COUNTS_COLS, minHeight: 36, ...(isCountsExpanded ? { alignItems: "start" } : {}) }}>
                      {rowNum(i)}
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                          <span
                            data-testid={`counts-group-label-${i}`}
                            title={angle === "former" ? titles.join(", ") : ps.label}
                            style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >{angle === "former" ? (titles[0] || ps.label) : ps.label}</span>
                          {angle !== "former" && (
                            <span style={{
                              fontSize: 10, fontFamily: "var(--font-data)", padding: "1px 5px", borderRadius: 4, whiteSpace: "nowrap", flex: "none",
                              background: ps.linked ? "rgba(45,212,191,.08)" : "rgba(224,160,80,.08)",
                              color: ps.linked ? "var(--accent)" : "var(--warning)",
                              border: `1px solid ${ps.linked ? "rgba(45,212,191,.28)" : "rgba(224,160,80,.28)"}`,
                            }}>
                              {ps.linked ? "exact" : "name match"}
                            </span>
                          )}
                        </span>
                        {count != null && !pr.error && angle !== "former" && (
                          <span style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                            <span style={{ height: 3, borderRadius: 2, background: "linear-gradient(90deg,#0e7490,#2dd4bf)", width: Math.max(2, Math.round((count / maxCount) * 120)) }} />
                            <span style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "var(--text-4)" }}>
                              {totalMatch ? Math.round((count / totalMatch) * 100) : 0}%
                            </span>
                          </span>
                        )}
                      </span>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, minWidth: 0 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {titles.length > 0
                            ? <TitleChips titles={titles} expanded={isCountsExpanded} onMore={angle === "former" ? () => setCountsExpandedGroups(eg => new Set([...eg, i])) : null} moreTestId={`counts-group-more-${i}`} />
                            : <span style={{ fontSize: 11, color: "var(--text-5)" }}>—</span>}
                        </div>
                        {angle === "former" && (
                          <button
                            type="button"
                            data-testid={`counts-group-expand-${i}`}
                            onClick={() => setCountsExpandedGroups(eg => { const n = new Set(eg); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                            title={isCountsExpanded ? "Collapse" : "Expand"}
                            style={{ fontSize: 11, color: "var(--text-4)", background: "none", border: "none", padding: "2px", cursor: "pointer", flex: "none" }}
                          >{isCountsExpanded ? "⊟" : "⊞"}</button>
                        )}
                      </div>
                      <span style={{ textAlign: "right", minWidth: 0 }}>
                        <span style={{ display: "block", fontFamily: "var(--font-data)", fontSize: 13, fontWeight: 500, color: matchColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          title={pr.error ? "Count failed — set a number to bring some in anyway" : undefined}>
                          {pr.error
                            ? "Count failed"
                            : (count === 0 || count == null)
                            ? (ps.companyUrl
                                ? <a href={ps.companyUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>0 found — check the LinkedIn link</a>
                                : "0 found — check the LinkedIn link"
                              )
                            : fmtN(count)}
                        </span>
                        {isOverLimit && (
                          <span style={{ display: "block", fontSize: 10, color: "var(--warning)", whiteSpace: "nowrap" }}>
                            {fmtN(count - 2500)} past the 2,500 cap
                          </span>
                        )}
                      </span>
                      <span style={{ display: "flex", justifyContent: "flex-end" }}>
                        {angle === "former" ? (
                          <span style={{ fontFamily: "var(--font-data)", fontSize: 12, color: enabled ? "var(--text-2)" : "var(--text-4)" }}>
                            {enabled && count != null && !pr.error ? fmtN(Math.min(count, 2500)) : "—"}
                          </span>
                        ) : (
                          <span className="nsp-stepper">
                            <button
                              type="button"
                              aria-label="Bring in fewer"
                              onClick={() => setCap(i, Math.max(0, Math.min(cap, Math.max(0, cap - 100))))}
                            >−</button>
                            <input
                              type="number"
                              min={0}
                              max={2500}
                              value={fetchCaps[i] ?? ""}
                              onChange={(e) =>
                                setFetchCaps((p) => p.map((v, j) => j === i ? Number(e.target.value) : v))
                              }
                              title="Bring in"
                            />
                            <button
                              type="button"
                              aria-label="Bring in more"
                              onClick={() => setCap(i, Math.min(2500, cap + 100))}
                            >+</button>
                          </span>
                        )}
                      </span>
                      <span style={{ display: "flex", justifyContent: "flex-end" }}>
                        {angle === "former" && (
                          <button
                            data-testid={`group-toggle-${i}`}
                            type="button"
                            onClick={() => {
                              const base = groupEnabled.length > 0 ? groupEnabled : new Array(probeSets.length).fill(true);
                              const next = base.map((v, j) => j === i ? !v : v);
                              setGroupEnabled(next);
                              const anyEnabled = next.some(Boolean);
                              if (autoRecountTimerRef.current) clearTimeout(autoRecountTimerRef.current);
                              if (!anyEnabled) {
                                autoRecountTimerRef.current = null;
                                setUniqueNeedsUpdate(false);
                              } else {
                                setUniqueNeedsUpdate(true);
                                autoRecountTimerRef.current = setTimeout(() => {
                                  autoRecountTimerRef.current = null;
                                  setAutoRecountSignal(s => s + 1);
                                }, 1200);
                              }
                            }}
                            style={{
                              fontSize: 11, fontFamily: "var(--font-data)", height: 24, padding: "0 10px", borderRadius: 6, cursor: "pointer",
                              background: enabled ? "rgba(45,212,191,.12)" : "rgba(255,255,255,.05)",
                              color: enabled ? "var(--accent)" : "var(--text-3)",
                              border: `1px solid ${enabled ? "rgba(45,212,191,.3)" : "var(--line)"}`,
                            }}
                          >
                            {enabled ? "On" : "Off"}
                          </button>
                        )}
                      </span>
                    </div>
                    {isOverLimit && (
                      <div style={{ fontSize: 11, color: "var(--warning)", paddingLeft: 48, marginTop: 4 }}>
                        LinkedIn returns at most 2,500 per search, so the rest of this group won't be reached. Tighten its titles to reach them.
                      </div>
                    )}
                    {showNudge && (
                      <div style={{ fontSize: 11, color: "var(--warning)", paddingLeft: 48, marginTop: 4 }}>
                        Outsized for a name match — same-name collision? Link its LinkedIn page and re-check.
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Helper row */}
              <div style={{ padding: "10px 16px", fontSize: 12, color: "var(--text-4)" }}>
                {angle === "former"
                  ? "Groups overlap, so matches add up to more than the unique total. Each person is pulled once, 1 credit each."
                  : <><Mono color="var(--text-2)">{fmtN(totalMatch)}</Mono> people match. Caps start at the full match (max 2,500) and take the first N in the vendor's search order — ranking happens after the leads are in. 1 credit per profile.</>}
              </div>
            </div></div>

            {totalMatch > 5000 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderTop: "1px solid var(--line-soft)", background: "rgba(255,190,50,.05)", fontSize: 12, color: "var(--text-2)" }}>
                <span style={{ flex: 1 }}>Lots of matches — Rasp can trim the roles to the most specific titles.</span>
                <button
                  data-testid="tighten-btn"
                  className="r-btn r-btn-ghost"
                  onClick={handleTighten}
                  disabled={tightenLoading}
                  style={{ whiteSpace: "nowrap", height: 28 }}
                >
                  {tightenLoading ? "Tightening…" : "Tighten search"}
                </button>
              </div>
            )}
            {tightenError && (
              <div data-testid="tighten-error" style={{ ...noteRow, borderBottom: "none", borderTop: "1px solid var(--line-soft)", color: "var(--warning)" }}>{tightenError}</div>
            )}
          </div>

          {/* Summary strip */}
          {(() => {
            const cpp = creditsMe ? (creditsMe.credits_per_profile ?? 1) : 1;
            const needed = totalFetch * cpp;
            const have = creditsMe ? (creditsMe.balance ?? 0) : null;
            const creditShort = !!creditsMe && !isFormerStale && have < needed;
            const isDisabled = isFormerStale || totalFetch === 0 || (!!creditsMe && have < needed);
            const statLabel = { ...eyebrow };
            const statValue = { fontFamily: "var(--font-data)", fontSize: 20, fontWeight: 500, lineHeight: 1.2, color: "var(--text-1)" };
            return (
              <div style={{
                display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", padding: "14px 18px",
                border: "1px solid rgba(45,212,191,.3)", borderRadius: 12,
                background: "linear-gradient(90deg, rgba(14,116,144,.30), rgba(45,212,191,.06))",
              }}>
                <div>
                  <div style={statLabel}>{angle === "former" ? "Unique people" : "You'll get"}</div>
                  <div style={statValue}>
                    {angle === "former"
                      ? <span data-testid="unique-total">{isFormerStale ? "—" : (uniqueTotal != null ? fmtN(uniqueTotal) : "—")}</span>
                      : fmtN(totalFetch)}
                    {angle === "former" && uniqueNeedsUpdate && (
                      <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 400, marginLeft: 8 }}>Updating…</span>
                    )}
                  </div>
                </div>
                {creditsMe && (
                  <div>
                    <div style={statLabel}>Credits</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span data-testid="credits-preview" style={{ ...statValue, fontVariantNumeric: "tabular-nums" }}>
                        {isFormerStale ? "—" : <>{needed.toLocaleString('en-US')} <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 400 }}>of {have.toLocaleString('en-US')}</span></>}
                      </span>
                      {creditShort && (
                        <span data-testid="credits-short" style={{ fontSize: 12, color: "var(--red, #f87171)" }}>
                          Not enough credits. Ask your admin for more.
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ flex: 1 }} />
                <button data-testid="stop-here-link" onClick={() => setStage("done")} className="r-btn r-btn-ghost" style={{ border: "none", padding: "0 6px", color: "var(--text-3)" }} title="Keeps the probe leads in the batch so nothing is lost">Stop here</button>
                {angle === "former" && hasUniqueError && (
                  <button
                    data-testid="update-unique"
                    className="r-btn r-btn-ghost"
                    onClick={handleUpdateUnique}
                    disabled={uniqueUpdating}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {uniqueUpdating ? "Updating…" : "Retry"}
                  </button>
                )}
                <button data-testid="back-to-plan-btn" onClick={handleBackToPlan} className="r-btn r-btn-ghost">Back to plan</button>
                <button
                  className="r-btn r-btn-primary"
                  onClick={handlePull}
                  disabled={isDisabled}
                  style={{ ...primaryBtn, ...(isDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                >
                  {isFormerStale ? "Bring in leads" : <>Bring in <span style={{ fontFamily: "var(--font-data)", fontSize: 12, fontWeight: 500 }}>{fmtN(totalFetch)}</span> leads</>}
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── DONE: one green-tinted block ── */}
      {shownStage === "done" && (
        <div style={stageBody}>
          <div style={{
            display: "flex", flexDirection: "column", gap: 14, padding: 18, borderRadius: 12,
            border: `1px solid ${doneCancelled ? "var(--line)" : "rgba(69,196,154,.3)"}`,
            background: doneCancelled ? "rgba(255,255,255,.02)" : "rgba(69,196,154,.05)",
          }}>
            {rankStatus && rankStatus.batchId === batchId && rankStatus.state === 'ranking' ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div>
                    <div data-testid="auto-rank-progress" style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em", color: "var(--text-1)" }}>
                      {rankStatus.msg || "Ranking your leads…"}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-3)" }}>Ranking to the top — just a moment.</div>
                  </div>
                </div>
              </>
            ) : rankStatus && rankStatus.batchId === batchId && rankStatus.state === 'error' ? (
              <>
                <div data-testid="auto-rank-error" style={{ fontSize: 13, color: "var(--negative)" }}>
                  {rankStatus.msg || "Ranking failed"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button
                    data-testid="auto-rank-retry"
                    className="r-btn r-btn-primary"
                    onClick={() => { autoRankCalledRef.current = false; onOpenBatch(batchId, { rank: true }); }}
                    style={primaryBtn}
                  >
                    Try again
                  </button>
                  <button onClick={resetAll} className="r-btn r-btn-ghost">Start another search</button>
                </div>
              </>
            ) : pullResults.length > 0 ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: "50%", background: "var(--positive)",
                    display: "flex", alignItems: "center", justifyContent: "center", flex: "none", animation: "nsp-popIn .4s ease-out",
                  }}>
                    <Tick color="#04201d" w={4} h={8} stroke={2} mt={-2} />
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em", color: "var(--text-1)" }}>All done — your leads are in</span>
                  <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                    Batch <Mono>#{batchId}</Mono> · <span style={{ color: "var(--text-1)" }}>{batchLabel}</span> — find it below.
                  </span>
                </div>
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1, border: "1px solid var(--line)",
                  borderRadius: 10, background: "var(--line)", overflow: "hidden", maxWidth: 560,
                }}>
                  {[
                    ["Staged", totalStaged, "var(--text-1)"],
                    ["Runs", pullResults.length, "var(--text-1)"],
                    ["Overlaps deduped", totalDupes, "var(--warning)"],
                  ].map(([k, v, c]) => (
                    <div key={k} style={{ padding: "12px 14px", background: "rgba(10,22,40,.6)" }}>
                      <div style={{ ...eyebrow, marginBottom: 6 }}>{k}</div>
                      <div style={{ fontFamily: "var(--font-data)", fontSize: 26, fontWeight: 500, lineHeight: 1, letterSpacing: "-.02em", color: c }}>{fmtN(v)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 560 }}>
                  {pullResults.map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: r.error ? "var(--negative)" : "var(--text-3)" }}>
                      <span style={{ width: 14, display: "flex", justifyContent: "center", flex: "none" }}>
                        {r.error ? "×" : <Tick color="var(--positive)" w={4} h={8} mt={-2} />}
                      </span>
                      <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {pullRuns[i] ? buildRunLabel(pullRuns[i]) : `Run ${i + 1}`}
                      </span>
                      <span style={{ fontFamily: "var(--font-data)", fontSize: 11 }}>
                        {r.error ? r.error : <><span style={{ color: "var(--text-2)" }}>{fmtN(r.staged)}</span> staged · {fmtN(r.dupes)} dupes</>}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {onOpenBatch && batchId != null && (
                    <button
                      className="r-btn r-btn-primary"
                      onClick={() => onOpenBatch(batchId, { rank: true })}
                      style={primaryBtn}
                    >
                      Rank my leads →
                    </button>
                  )}
                  <button onClick={resetAll} className="r-btn r-btn-ghost">Start another search</button>
                  <span style={{ fontSize: 12, color: "var(--text-4)" }}>
                    Ranking scores every lead against your topic{angle === "former" ? " — former-employee batches also get a recruit-quality score" : ""}. Then import the shortlist into a project.
                  </span>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: "var(--text-3)" }}>
                  Search cancelled — probe leads remain in the batch{signoffTarget ? <> under <span style={{ color: "var(--text-1)" }}>{batchLabel}</span></> : null}.
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={resetAll} className="r-btn r-btn-ghost">Start another search</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
